import type { ErrorComponentProps } from "@tanstack/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { SiteHeader } from "@/components/site/SiteHeader";
import { AdminBar } from "@/components/site/AdminBar";
import { SiteFooter } from "@/components/site/SiteFooter";
import { FloatingAssessmentCTA } from "@/components/site/FloatingAssessmentCTA";
import logoSrc from "@/assets/lel-logo.png";
const logo = { url: logoSrc };

function NotFoundComponent() {
  return (
    <div className="flex min-h-[70vh] items-center justify-center bg-lavender px-4">
      <div className="max-w-md text-center">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">404</p>
        <h1 className="mt-3 font-display text-4xl text-ink">Page not found</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center rounded-[6px] bg-[#693D94] px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-brand"
          >
            Return home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: ErrorComponentProps) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-[70vh] items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="font-display text-2xl text-ink">This page didn't load</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          Something went wrong on our end. You can try again or head home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center rounded-[6px] bg-[#693D94] px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-brand"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center rounded-[6px] border border-border bg-background px-5 py-2.5 text-sm font-semibold text-foreground"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Leadership Training for Growing Companies | Leadership Edge Live" },
      {
        name: "description",
        content:
          "Live, practical leadership training your managers can use the same week. Built for 50 to 1000 person companies with no training department.",
      },
      { name: "author", content: "Leadership Edge Live" },
      {
        property: "og:title",
        content: "Leadership Training for Growing Companies | Leadership Edge Live",
      },
      {
        property: "og:description",
        content:
          "Live, practical leadership training your managers can use the same week. Built for 50 to 1000 person companies with no training department.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      {
        name: "twitter:title",
        content: "Leadership Training for Growing Companies | Leadership Edge Live",
      },
      {
        name: "twitter:description",
        content:
          "Live, practical leadership training your managers can use the same week. Built for 50 to 1000 person companies with no training department.",
      },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;600;700&family=Inter:wght@400;500;600;700&family=Poppins:wght@300;400;500;600;700&display=swap",
      },
      { rel: "icon", type: "image/x-icon", href: "/favicon.ico" },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  useEffect(() => {
    const key = "__lel_reloaded_for_stale_chunk";
    // The page loaded successfully, so allow a future stale-bundle reload again.
    sessionStorage.removeItem(key);

    const onChunkError = (event: Event) => {
      const message =
        (event as PromiseRejectionEvent).reason?.message || (event as ErrorEvent).message || "";
      if (
        typeof message === "string" &&
        (message.includes("Failed to fetch dynamically imported module") ||
          message.includes("Importing a module script failed") ||
          message.includes("error loading dynamically imported module"))
      ) {
        if (!sessionStorage.getItem(key)) {
          sessionStorage.setItem(key, "1");
          // Cache-bust so the browser re-fetches the current asset manifest.
          const url = new URL(window.location.href);
          url.searchParams.set("_r", Date.now().toString(36));
          window.location.replace(url.toString());
        }
      }
    };
    window.addEventListener("error", onChunkError);
    window.addEventListener("unhandledrejection", onChunkError);
    return () => {
      window.removeEventListener("error", onChunkError);
      window.removeEventListener("unhandledrejection", onChunkError);
    };
  }, []);

  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const path = pathname === "/" ? "/" : pathname.replace(/\/+$/, "");
  // The /grow landing page and /start checkout ship their own minimal header and footer.
  const bareChrome = path === "/grow" || path.startsWith("/grow/") || path === "/start";

  if (bareChrome) {
    return (
      <QueryClientProvider client={queryClient}>
        <Outlet />
      </QueryClientProvider>
    );
  }

  const isHome = path === "/";

  return (
    <QueryClientProvider client={queryClient}>
      <AdminBar />
      <div className="relative flex min-h-screen flex-col">
        <SiteHeader transparent={isHome} overlay={isHome} />

        <main className="flex-1">
          <Outlet />
        </main>
        <SiteFooter />
        <FloatingAssessmentCTA />
      </div>
    </QueryClientProvider>
  );
}
